import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ModalBasic from "./ModalBasic.jsx";
import playStorePopup from "../../img/playstore_popup.png";
import qrAppStoreIOS from "../../img/qr_appstore_ios.png";
import qrPlayStoreAndroid from "../../img/qr_playstore_android.png";
import closeButton from "../../img/close_button.png";
import { trackNaverAppDownloadIntent } from "../../utils/naverConversion.js";

const ModalAppDownload = ({ isOpen, onClose }) => {
  const [currentView, setCurrentView] = useState('main'); // 'main', 'qr-ios', 'qr-android'
  const [isMobile, setIsMobile] = useState(false);
  const [hideForever, setHideForever] = useState(false);

  // 화면 크기 감지
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 430);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // UserAgent 기반 디바이스 감지
  const detectDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    } else if (/android/.test(userAgent)) {
      return 'android';
    }
    return 'unknown';
  };

  // 스토어 이동 함수
  const redirectToStore = (storeType) => {
    const urls = {
      ios: 'https://apps.apple.com/kr/app/%EB%A1%9C%EC%9A%B0%EB%A1%9C%EC%9A%B0/id6748278644',
      android: 'https://play.google.com/store/apps/details?id=com.lawlow.lawlow_app&pli=1'
    };

    if (urls[storeType]) {
      trackNaverAppDownloadIntent({ id: storeType });
      window.open(urls[storeType], '_blank');
    }
  };

  // 모바일에서 앱 설치 바로가기 클릭
  const handleMobileInstall = () => {
    const device = detectDevice();
    if (device === 'ios') {
      redirectToStore('ios');
    } else if (device === 'android') {
      redirectToStore('android');
    } else {
      // 디바이스를 감지할 수 없는 경우 Android로 기본 설정
      redirectToStore('android');
    }
  };

  // QR 코드 표시
  const showQRCode = (type) => {
    trackNaverAppDownloadIntent({ id: `qr-${type}` });
    setCurrentView(`qr-${type}`);
  };

  // 메인 화면으로 돌아가기
  const goBackToMain = () => {
    setCurrentView('main');
  };

  // 모달 닫기 (다시 보지 않기 적용)
  const handleClose = () => {
    if (hideForever) {
      localStorage.setItem('hideAppDownloadModal', 'forever');
    }
    setHideForever(false);
    setCurrentView('main');
    onClose();
  };

  // 다시 보지 않기 체크박스 토글
  const toggleHideForever = () => {
    setHideForever(!hideForever);
  };

  // 메인 화면 렌더링
  const renderMainView = () => (
    <MainContainer isMobile={isMobile}>
      <ImageContainer isMobile={isMobile}>
        <TopImage src={playStorePopup} alt="앱 다운로드" isMobile={isMobile} />
        <CloseButton onClick={handleClose}>
          <img
            src={closeButton}
            alt="닫기 버튼"
            width="32px"
            height="32px"
          />
        </CloseButton>
      </ImageContainer>
      
      <ContentSection isMobile={isMobile}>
        <Title>로우로우 앱을 만나보세요!
        <br /> 이전보다 더욱 편리하게 이용할 수 있어요.</Title>
        
        {isMobile ? (
          // 모바일: 단일 버튼
          <MobileButtonContainer>
            <InstallButton onClick={handleMobileInstall}>
              앱 설치 바로가기
            </InstallButton>
          </MobileButtonContainer>
        ) : (
          // PC: QR 버튼들
          <PCButtonContainer>
            <QRButton onClick={() => showQRCode('ios')}>
              앱스토어 설치 QR
            </QRButton>
            <QRButton onClick={() => showQRCode('android')}>
              플레이스토어 설치 QR
            </QRButton>
          </PCButtonContainer>
        )}
        
        <CheckboxContainer>
          <Checkbox
            type="checkbox"
            checked={hideForever}
            onChange={toggleHideForever}
          />
          <CheckboxLabel>다시 보지 않기</CheckboxLabel>
        </CheckboxContainer>
      </ContentSection>
    </MainContainer>
  );

  // QR 코드 화면 렌더링
  const renderQRView = () => {
    const isIOS = currentView === 'qr-ios';
    const qrImage = isIOS ? qrAppStoreIOS : qrPlayStoreAndroid;
    const storeName = isIOS ? '앱스토어' : '플레이스토어';
    
    return (
      <QRContainer isMobile={isMobile}>
        <ImageContainer>
          <TopImageQR src={qrImage} alt={`${storeName} QR 코드`} isMobile={isMobile} />
          <CloseButton onClick={handleClose}>
            <img
              src={closeButton}
              alt="닫기 버튼"
              width="32px"
              height="32px"
            />
          </CloseButton>
        </ImageContainer>
        
        <QRContentSection currentView={currentView}>
          <QRTitle>
            휴대폰 기본 카메라로 상단 QR을 스캔하면<br />
            {storeName} 설치 화면으로 이동합니다.
          </QRTitle>
        </QRContentSection>
      </QRContainer>
    );
  };

  // 현재 뷰에 따른 컨텐츠 렌더링
  const renderContent = () => {
    if (currentView === 'main') {
      return renderMainView();
    } else {
      return renderQRView();
    }
  };

  return (
    <ModalBasic
      isOpen={isOpen}
      onClose={handleClose}
      width={isMobile ? "312px" : "400px"}
      height={
        isMobile 
          ? "322px" 
          : currentView === 'main' 
            ? "452px" 
            : "377px"
      }
    >
      {renderContent()}
    </ModalBasic>
  );
};

// 스타일 컴포넌트들
const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const QRContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TopImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px 12px 0 0;
`;

const TopImageQR = styled.img`
  width: 100%;
  height: ${props => props.isMobile ? '152px' : '265px'};
  object-fit: cover;
  border-radius: 12px 12px 0 0;
`;

const ContentSection = styled.div`
  padding: ${props => props.isMobile ? '12px 24px 16px' : '20px 32px 16px'};
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const QRContentSection = styled.div`
  padding: ${props => {
    // PC 전용 QR 화면 - currentView에 따른 패딩 적용
    if (props.currentView === 'qr-ios') {
      return '90px 32px 16px'; // 앱스토어 QR
    } else if (props.currentView === 'qr-android') {
      return '90px 32px 16px'; // 플레이스토어 QR
    }
    return '20px 32px 16px'; // 기본값
  }};
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
`;


const Title = styled.h2`
  margin: 0 0 8px 0;
  text-align: center;
  color: #000;
  text-align: center;
  font-family: Pretendard;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px; /* 160% */
  letter-spacing: -0.4px;

  @media (max-width: 430px) {
    font-size: 16px;
    line-height: 26px; 
    letter-spacing: -0.32px;
  }
`;

const QRTitle = styled.p`
  margin: 0;
  color: #000;
  text-align: center;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 32px; 
  letter-spacing: -0.4px;
`;

const PCButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const MobileButtonContainer = styled.div`
  width: 100%;
  margin-bottom: 14px;
`;

const QRButton = styled.button`
  padding: 8px 15.27px;
  background: #F2F2F2;
  border: none;
  border-radius: 8px;
  font-family: Pretendard;
  font-size: 16px;
  font-weight: 500;
  line-height: 30px; 
  letter-spacing: -0.32px;
  color: rgba(0, 0, 0, 0.60);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    border-color: #ccc;
  }
`;

const InstallButton = styled.button`
  width: 100%;
  padding: 5px 12px;
  background: #062515;
  border: none;
  border-radius: 8px;
  font-family: Pretendard;
  color: #00F370;
  font-size: 14px;
  font-weight: 600;
  line-height: 30px;
  letter-spacing: -0.28px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #1e3d1a;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.20);
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  color: rgba(0, 0, 0, 0.30);
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.28px;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.isMobile ? '152px' : '195px'};
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 10;
  
  &:hover {
    opacity: 0.8;
  }
`;

const QRImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
`;

const QRImage = styled.img`
  width: 160px;
  height: 160px;
  object-fit: contain;
`;

export default ModalAppDownload;
